import type { UseFormReturn } from 'react-hook-form'
import { Calendar, Award, Clock, UserPlus, ShoppingCart } from 'lucide-react'
import { CardSelector } from './CardSelector'
import type { CardOption } from './CardSelector'
import {
  TRIGGER_TYPE_LABELS,
  TRIGGER_TYPE_DESCRIPTIONS,
  RECURRENCE_LABELS,
  CATEGORY_OPTIONS,
  type StepTriggerData,
  type TriggerType,
} from '../types'

const TRIGGER_CARDS: CardOption<TriggerType>[] = [
  { value: 'birthday_upcoming', label: TRIGGER_TYPE_LABELS.birthday_upcoming, description: TRIGGER_TYPE_DESCRIPTIONS.birthday_upcoming, icon: <Calendar size={24} /> },
  { value: 'category_reached', label: TRIGGER_TYPE_LABELS.category_reached, description: TRIGGER_TYPE_DESCRIPTIONS.category_reached, icon: <Award size={24} /> },
  { value: 'recurring_schedule', label: TRIGGER_TYPE_LABELS.recurring_schedule, description: TRIGGER_TYPE_DESCRIPTIONS.recurring_schedule, icon: <Clock size={24} /> },
  { value: 'client_created', label: TRIGGER_TYPE_LABELS.client_created, description: TRIGGER_TYPE_DESCRIPTIONS.client_created, icon: <UserPlus size={24} /> },
  { value: 'purchase_completed', label: TRIGGER_TYPE_LABELS.purchase_completed, description: TRIGGER_TYPE_DESCRIPTIONS.purchase_completed, icon: <ShoppingCart size={24} /> },
]

interface StepTriggerFormProps {
  form: UseFormReturn<StepTriggerData>
  onSubmit: () => void
}

export function StepTriggerForm({ form, onSubmit }: StepTriggerFormProps) {
  const watchedTriggerType = form.watch('trigger_type')

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de la regla *</label>
        <input {...form.register('name')} className="input" placeholder="Ej: Regalo cumpleaños Bronce" />
        {form.formState.errors.name && (
          <p className="text-red-500 text-sm mt-1">{form.formState.errors.name.message}</p>
        )}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Descripción <span className="text-gray-400 font-normal">(visible para el cliente)</span>
        </label>
        <textarea {...form.register('description')} className="input" rows={2} placeholder="Ej: Cupón de 10% de descuento para tu cumpleaños" />
      </div>

      {/* Internal notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notas internas <span className="text-gray-400 font-normal">(solo admins)</span>
        </label>
        <textarea {...form.register('internal_notes')} className="input" rows={2} placeholder="Ej: Solo bronce, uso único anual" />
      </div>

      {/* Trigger type card selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Tipo de gatillo *</label>
        <CardSelector<TriggerType>
          options={TRIGGER_CARDS}
          value={watchedTriggerType}
          onChange={(val) => form.setValue('trigger_type', val, { shouldValidate: true })}
          error={form.formState.errors.trigger_type?.message}
        />
      </div>

      {/* Dynamic trigger config */}
      {watchedTriggerType === 'birthday_upcoming' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Días de anticipación</label>
          <input type="number" {...form.register('trigger_days_before')} className="input w-32" min={1} />
        </div>
      )}

      {watchedTriggerType === 'category_reached' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nivel requerido</label>
          <select {...form.register('trigger_category')} className="input w-48">
            {CATEGORY_OPTIONS.map((cat) => (
              <option key={cat} value={cat} className="capitalize">{cat}</option>
            ))}
          </select>
        </div>
      )}

      {watchedTriggerType === 'recurring_schedule' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Recurrencia</label>
            <select {...form.register('trigger_recurrence')} className="input">
              {Object.entries(RECURRENCE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Zona horaria</label>
            <select {...form.register('trigger_timezone')} className="input">
              <option value="America/Santiago">America/Santiago</option>
              <option value="UTC">UTC</option>
            </select>
          </div>
        </div>
      )}

      {watchedTriggerType === 'purchase_completed' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Canal</label>
          <select {...form.register('trigger_channel')} className="input w-48">
            <option value="all">Todos</option>
            <option value="web">Web</option>
            <option value="store">Tienda</option>
          </select>
        </div>
      )}

      <div className="flex justify-end">
        <button type="submit" className="btn btn-primary">
          Siguiente
        </button>
      </div>
    </form>
  )
}
